// The flora tiles the world is decorated with, and the one arithmetic fact needed to recognise them
// again on the client.
//
// These indices MATCH the game's own procedural biome generators (packages/tools/src/mapgen/biomes),
// which is what makes ember decoration read like the rest of the world. Most live in the
// "lpc-flowers-plants-fungi-wood" tileset; the small terrain flowers live in "lpc-terrain".
//
// Layer conventions follow the game too: flat vegetation the player walks in front of (bushes, flower
// patches, small flowers) goes on map2, tall grass that y-sorts like a plant -- you can stand behind
// it -- goes on the entity-tile surface (map3, same layer as trees), and fine forest litter goes on
// map4. All are purely cosmetic and non-blocking.

export const FLORA_TILESET = "lpc-flowers-plants-fungi-wood"
export const TERRAIN_TILESET = "lpc-terrain"

export const LONG_GRASS_FIELD = 227 // dense tall-grass fill -- addLongGrass' 95% pick
export const LONG_GRASS_TUFTS = [160, 161, 162, 177, 178] // sparser tufts -- addLongGrass' 5% variety
export const BUSH_TILES = [376, 412, 412, 408] // leafy/round shrubs, per Plains biome (412 weighted)
export const FLOWER_PATCH_TILES = [29, 45] // yellow / white flower patches, per Plains biome
export const SMALL_FLOWER_TILES = [355, 356, 357] // small red wildflowers (lpc-terrain), per Plains
export const TWIG_TILES = [452, 436, 421] // forest-floor twigs, per Forest biome
export const MUSHROOM_TILES = [353, 354, 355] // forest-floor mushrooms (the mushroom items' tiles)

/**
 * The texture id the CLIENT renders a given tileset tile under -- which is not the number written
 * above, and the difference is a silent one.
 *
 * A builder places a tile by its position within a tileset, and the map stores the global id
 * `firstgid + tilePos` (BaseMapBuilder). The client resolves that back with
 * TileMapMetaProvider.resolveTilesetTileId, which returns `gid - firstGid + 1`. The two conventions
 * are off by one, so a client asking for flora by its authoring index looks up a texture that exists,
 * belongs to the neighbouring tile, and fails at nothing -- it just never matches. Anything on the
 * client that needs to recognise a placed tile goes through here, and floraTextureIds is pinned by a
 * test against a really generated room.
 */
export const floraTextureId = (tilesetId: string, tilePos: number): string => `${tilesetId}-${tilePos + 1}`

export const floraTextureIds = (tilesetId: string, tilePositions: number[]): string[] =>
    tilePositions.map(tilePos => floraTextureId(tilesetId, tilePos))

/**
 * The tiles that bend in the wind, with how far the tip of each leans at a full gust, in world px.
 *
 * Long grass only, for now, and deliberately: it is tall, soft, and drawn as loose blades, so a shear
 * reads as it bending. A tree is a trunk -- shearing the whole sprite makes it look made of rubber --
 * and the map's tree and bush shadows are baked into the ground art and cannot follow, so anything
 * with a strong shadow has to stay nearly still or it slides off its own shadow. The dense fill leans
 * furthest; the sparser tufts are shorter and stiffer.
 */
export const SWAY_TILES: Array<{ tilesetId: string; tilePositions: number[]; lean: number }> = [
    { tilesetId: FLORA_TILESET, tilePositions: [LONG_GRASS_FIELD], lean: 4.5 },
    { tilesetId: FLORA_TILESET, tilePositions: LONG_GRASS_TUFTS, lean: 2.8 },
]

// texture id -> lean, ready for the client to look a sprited cell up by the id it was rendered with.
export const SWAY_LEAN_BY_TEXTURE_ID: Record<string, number> = SWAY_TILES.reduce(
    (acc, { tilesetId, tilePositions, lean }) => {
        floraTextureIds(tilesetId, tilePositions).forEach(id => {
            acc[id] = lean
        })
        return acc
    },
    {} as Record<string, number>,
)

